import React, { useState, useEffect, useRef } from 'react';
import {
    Palette, Globe, ExternalLink, Copy, Check, Loader2, Save,
    Upload, Image, Lock, Users, AlertCircle, CheckCircle, Crown, X, Link
} from 'lucide-react';
import { getBrandingSettings, updateBrandingSettings, uploadBrandingImage, deleteBrandingImage } from '../../api/branding';

// Color picker input component
const ColorInput = ({ label, value, onChange, disabled = false }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
        <div className="flex items-center gap-2">
            <input
                type="color"
                value={value || '#000000'}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className={`w-10 h-10 rounded-lg border border-slate-300 dark:border-slate-600 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            />
            <input
                type="text"
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder="#000000"
                disabled={disabled}
                className="flex-1 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            />
        </div>
    </div>
);

// Text input component
const TextInput = ({ label, value, onChange, placeholder, type = 'text', multiline = false, disabled = false }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
        {multiline ? (
            <textarea
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                rows={3}
                disabled={disabled}
                className="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white resize-none disabled:opacity-50 disabled:cursor-not-allowed"
            />
        ) : (
            <input
                type={type}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                className="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            />
        )}
    </div>
);

// Image upload/URL input component with drag & drop
const ImageInput = ({ label, value, onChange, placeholder = 'Enter image URL', orgId, imageType = 'logo', disabled = false }) => {
    const [isUploading, setIsUploading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [showUrlInput, setShowUrlInput] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileSelect = async (file) => {
        if (!file || !orgId) return;

        // Validate file type
        const validTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon'];
        if (!validTypes.includes(file.type)) {
            setUploadError('Please upload a PNG, JPG, GIF, SVG, or ICO file.');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setUploadError('File is too large. Maximum size is 5MB.');
            return;
        }

        setIsUploading(true);
        setUploadError(null);

        try {
            const result = await uploadBrandingImage(orgId, file, imageType);
            onChange(result.url);
        } catch (err) {
            setUploadError(err.message || 'Failed to upload image');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (disabled) return;

        const file = e.dataTransfer.files[0];
        handleFileSelect(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (!disabled) setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleClick = () => {
        if (!disabled && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        handleFileSelect(file);
        // Reset input so same file can be selected again
        e.target.value = '';
    };

    const handleRemove = async () => {
        if (!orgId) {
            onChange('');
            return;
        }

        setIsDeleting(true);
        setUploadError(null);

        try {
            // Call delete API to remove from storage and clear branding settings
            await deleteBrandingImage(orgId, imageType);
            onChange('');
        } catch (err) {
            setUploadError(err.message || 'Failed to delete image');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>

            {/* Current Image Preview */}
            {value && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                    <div className="w-16 h-16 rounded-lg border border-slate-300 dark:border-slate-600 overflow-hidden bg-white dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                        <img
                            src={value}
                            alt=""
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{value}</p>
                    </div>
                    {!disabled && (
                        <button
                            onClick={handleRemove}
                            disabled={isDeleting}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                            title="Remove image"
                        >
                            {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                        </button>
                    )}
                </div>
            )}

            {/* Upload Zone */}
            {!value && (
                <div
                    onClick={handleClick}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`
                        relative flex flex-col items-center justify-center p-6 rounded-lg border-2 border-dashed transition-all
                        ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                        ${isDragging
                            ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20'
                            : 'border-slate-300 dark:border-slate-600 hover:border-sky-400 dark:hover:border-sky-500 bg-slate-50 dark:bg-slate-800/50'
                        }
                    `}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/svg+xml,image/x-icon"
                        onChange={handleFileChange}
                        className="hidden"
                        disabled={disabled}
                    />

                    {isUploading ? (
                        <div className="flex flex-col items-center gap-2">
                            <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
                            <span className="text-sm text-slate-500 dark:text-slate-400">Uploading...</span>
                        </div>
                    ) : (
                        <>
                            <Upload className="w-8 h-8 text-slate-400 dark:text-slate-500 mb-2" />
                            <p className="text-sm text-slate-600 dark:text-slate-300 font-medium">
                                Click to upload or drag and drop
                            </p>
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                                PNG, JPG, GIF, SVG or ICO (max 5MB)
                            </p>
                        </>
                    )}
                </div>
            )}

            {/* URL Input Toggle */}
            {!value && !isUploading && (
                <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                    <button
                        onClick={() => setShowUrlInput(!showUrlInput)}
                        className="text-xs text-slate-500 dark:text-slate-400 hover:text-sky-600 dark:hover:text-sky-400 flex items-center gap-1"
                        disabled={disabled}
                    >
                        <Link className="w-3 h-3" />
                        {showUrlInput ? 'Hide URL input' : 'Or enter URL'}
                    </button>
                    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                </div>
            )}

            {/* URL Input Field */}
            {showUrlInput && !value && (
                <input
                    type="text"
                    value={''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    className="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white disabled:opacity-50"
                />
            )}

            {/* Error Message */}
            {uploadError && (
                <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {uploadError}
                </div>
            )}
        </div>
    );
};

// Copy button component
const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <button
            onClick={handleCopy}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            title="Copy"
        >
            {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
        </button>
    );
};

// Branding Preview component
const BrandingPreview = ({ branding, orgSlug }) => (
    <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Login Preview</span>
            {orgSlug && (
                <a
                    href={`https://${orgSlug}.cloudmortgage.io`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-sky-600 dark:text-sky-400 hover:underline"
                >
                    Open Portal <ExternalLink className="w-3 h-3" />
                </a>
            )}
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-sm border border-slate-200 dark:border-slate-700">
            {branding.logoUrl ? (
                <img src={branding.logoUrl} alt="Logo" className="h-8 mb-4 object-contain" />
            ) : (
                <div className="h-8 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-4" />
            )}
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                {branding.loginTitle || 'Welcome'}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                {branding.loginSubtitle || 'Sign in to your account'}
            </p>
            <div className="space-y-3 mb-4">
                <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700" />
                <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700" />
            </div>
            <button
                className="w-full py-2.5 rounded-lg text-white font-medium text-sm"
                style={{ backgroundColor: branding.primaryColor || '#0284c7' }}
            >
                Sign In
            </button>
        </div>
    </div>
);

const OrgPortalSettings = ({ organization, subscription, userRole }) => {
    // Only admin/owner can edit branding settings
    const canEdit = ['admin', 'owner'].includes(userRole?.toLowerCase());
    const [branding, setBranding] = useState({
        logoUrl: '',
        logoDarkUrl: '',
        faviconUrl: '',
        primaryColor: '#0284c7',
        secondaryColor: '#0ea5e9',
        accentColor: '#38bdf8',
        loginTitle: '',
        loginSubtitle: '',
        loginBackgroundUrl: '',
        welcomeMessage: '',
        companyName: '',
        supportEmail: '',
        supportPhone: '',
        privacyUrl: '',
        termsUrl: '',
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const orgId = organization?.id || organization?.org_id;
    const orgSlug = organization?.slug;

    // Check if user has portal feature (Pro+)
    const tier = subscription?.plan_id?.toLowerCase() || subscription?.tier?.toLowerCase() || 'free';
    const hasPortalAccess = ['pro', 'ultra', 'enterprise'].includes(tier);

    useEffect(() => {
        const loadBranding = async () => {
            if (!orgId || !hasPortalAccess) {
                setIsLoading(false);
                return;
            }
            try {
                const data = await getBrandingSettings(orgId);
                setBranding(data);
            } catch (err) {
                console.error('Failed to load branding:', err);
                setError('Failed to load branding settings');
            } finally {
                setIsLoading(false);
            }
        };
        loadBranding();
    }, [orgId, hasPortalAccess]);

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        setSuccess(false);
        try {
            await updateBrandingSettings(orgId, branding);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const updateField = (field, value) => {
        setBranding(prev => ({ ...prev, [field]: value }));
    };

    // Show upgrade prompt for non-Pro users
    if (!hasPortalAccess) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-6">
                    <Crown className="w-10 h-10 text-amber-500" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    Custom Portal requires Pro or higher
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mb-8">
                    Create a custom-branded portal at <strong>{orgSlug || 'yourcompany'}.cloudmortgage.io</strong> with your logo, colors, and login page.
                </p>
                <button className="px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-colors shadow-lg">
                    Upgrade to Pro
                </button>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Permission Warning for non-admins */}
            {!canEdit && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                        <p className="text-sm text-amber-700 dark:text-amber-300">
                            Only organization <strong>admins</strong> and <strong>owners</strong> can edit portal settings. Contact your administrator to make changes.
                        </p>
                    </div>
                </div>
            )}

            {/* Portal URL Banner */}
            <div className="bg-gradient-to-r from-sky-500/10 to-indigo-500/10 border border-sky-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-sky-500/20 rounded-lg">
                        <Globe className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-slate-900 dark:text-white">Your Portal URL</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Share this link with your team and clients</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg px-4 py-3 border border-slate-200 dark:border-slate-700">
                    <code className="flex-1 text-sm text-sky-700 dark:text-sky-300 font-medium">
                        https://{orgSlug || 'yourcompany'}.cloudmortgage.io
                    </code>
                    <CopyButton text={`https://${orgSlug || 'yourcompany'}.cloudmortgage.io`} />
                    <a
                        href={`https://${orgSlug}.cloudmortgage.io`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-sky-600 dark:text-sky-400 hover:bg-sky-100 dark:hover:bg-sky-900/30 rounded-lg transition-colors"
                    >
                        <ExternalLink className="w-4 h-4" />
                    </a>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Settings Column */}
                <div className="space-y-6">
                    {/* Logo Section */}
                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                            <Image className="w-4 h-4 text-slate-500" />
                            Logo & Branding
                        </h4>
                        <div className="space-y-4">
                            <ImageInput
                                label="Logo"
                                value={branding.logoUrl}
                                onChange={(v) => updateField('logoUrl', v)}
                                placeholder="https://example.com/logo.png"
                                orgId={orgId}
                                imageType="logo"
                                disabled={!canEdit}
                            />
                            <ImageInput
                                label="Logo (Dark Mode)"
                                value={branding.logoDarkUrl}
                                onChange={(v) => updateField('logoDarkUrl', v)}
                                placeholder="https://example.com/logo-dark.png"
                                orgId={orgId}
                                imageType="logo_dark"
                                disabled={!canEdit}
                            />
                            <ImageInput
                                label="Favicon"
                                value={branding.faviconUrl}
                                onChange={(v) => updateField('faviconUrl', v)}
                                placeholder="https://example.com/favicon.ico"
                                orgId={orgId}
                                imageType="favicon"
                                disabled={!canEdit}
                            />
                        </div>
                    </div>

                    {/* Colors Section */}
                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                            <Palette className="w-4 h-4 text-slate-500" />
                            Brand Colors
                        </h4>
                        <div className="grid grid-cols-3 gap-4">
                            <ColorInput
                                label="Primary"
                                value={branding.primaryColor}
                                onChange={(v) => updateField('primaryColor', v)}
                                disabled={!canEdit}
                            />
                            <ColorInput
                                label="Secondary"
                                value={branding.secondaryColor}
                                onChange={(v) => updateField('secondaryColor', v)}
                                disabled={!canEdit}
                            />
                            <ColorInput
                                label="Accent"
                                value={branding.accentColor}
                                onChange={(v) => updateField('accentColor', v)}
                                disabled={!canEdit}
                            />
                        </div>
                    </div>

                    {/* Login Page Section */}
                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                            <Lock className="w-4 h-4 text-slate-500" />
                            Login Page
                        </h4>
                        <div className="space-y-4">
                            <TextInput
                                label="Title"
                                value={branding.loginTitle}
                                onChange={(v) => updateField('loginTitle', v)}
                                placeholder="Welcome to Your Company"
                                disabled={!canEdit}
                            />
                            <TextInput
                                label="Subtitle"
                                value={branding.loginSubtitle}
                                onChange={(v) => updateField('loginSubtitle', v)}
                                placeholder="Sign in to your mortgage portal"
                                disabled={!canEdit}
                            />
                            <ImageInput
                                label="Background Image"
                                value={branding.loginBackgroundUrl}
                                onChange={(v) => updateField('loginBackgroundUrl', v)}
                                placeholder="https://example.com/background.jpg"
                                orgId={orgId}
                                imageType="background"
                                disabled={!canEdit}
                            />
                        </div>
                    </div>

                    {/* Company Info Section */}
                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-500" />
                            Company Information
                        </h4>
                        <div className="space-y-4">
                            <TextInput
                                label="Company Name"
                                value={branding.companyName}
                                onChange={(v) => updateField('companyName', v)}
                                placeholder={organization?.name || 'Your Company'}
                                disabled={!canEdit}
                            />
                            <TextInput
                                label="Support Email"
                                value={branding.supportEmail}
                                onChange={(v) => updateField('supportEmail', v)}
                                placeholder="support@yourcompany.com"
                                type="email"
                                disabled={!canEdit}
                            />
                            <TextInput
                                label="Support Phone"
                                value={branding.supportPhone}
                                onChange={(v) => updateField('supportPhone', v)}
                                placeholder="+1-555-123-4567"
                                type="tel"
                                disabled={!canEdit}
                            />
                            <TextInput
                                label="Privacy Policy URL"
                                value={branding.privacyUrl}
                                onChange={(v) => updateField('privacyUrl', v)}
                                placeholder="https://yourcompany.com/privacy"
                                type="url"
                                disabled={!canEdit}
                            />
                            <TextInput
                                label="Terms of Service URL"
                                value={branding.termsUrl}
                                onChange={(v) => updateField('termsUrl', v)}
                                placeholder="https://yourcompany.com/terms"
                                type="url"
                                disabled={!canEdit}
                            />
                        </div>
                    </div>
                </div>

                {/* Preview Column */}
                <div className="lg:sticky lg:top-4 h-fit">
                    <BrandingPreview branding={branding} orgSlug={orgSlug} />
                </div>
            </div>

            {/* Save Button */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
                <div>
                    {error && (
                        <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
                            <AlertCircle className="w-4 h-4" />
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 text-sm">
                            <CheckCircle className="w-4 h-4" />
                            Portal settings saved successfully!
                        </div>
                    )}
                </div>
                {canEdit && (
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center gap-2 px-6 py-2.5 bg-sky-600 hover:bg-sky-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                        {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save Changes
                    </button>
                )}
            </div>
        </div>
    );
};

export default OrgPortalSettings;
